import React, { useState, useMemo, useEffect } from "react";
import classNames from "classnames";
import {
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
} from "reactstrap";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import { useGetInventories } from "../../../api/inventory/service/index";
import Select from "components/Select";
import { useGetChannels } from "api/channels/service";
import { useTranslation } from "react-i18next";
import { useNotification } from "contexts/NotificationContext";
import { getActiveColorStyle } from "utils/colorUtils";
import { getSelectStyles } from "utils/selectStyles";
import {
  getTheme2FormFieldsCss,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";
import {
  buildInventorySearchColumns,
  mapInventorySearchResults,
  toVehicleSelectionRow,
} from "views/inventory/utils/inventorySearchTable";
import { getStickyStockTableStickyColumnCss } from "utils/reactTableTheme";

const getAddVehicleLayoutCss = () => `
.worksheet-form.worksheet-form--add-vehicle {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  max-height: calc(100vh - 140px);
  height: calc(100vh - 140px);
}
@supports (height: 100dvh) {
  .worksheet-form.worksheet-form--add-vehicle {
    max-height: calc(100dvh - 140px);
    height: calc(100dvh - 140px);
  }
}
.worksheet-form.worksheet-form--add-vehicle > .add-vehicle--layout,
.deals-drawer-add-vehicle > .add-vehicle--layout {
  flex: 1 1 auto;
  min-height: 0;
  height: auto;
  max-height: none;
}
.deals-drawer-add-vehicle {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.add-vehicle.add-vehicle--layout {
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: calc(100vh - 140px);
  height: calc(100vh - 140px);
  overflow: hidden;
  box-sizing: border-box;
}
@supports (height: 100dvh) {
  .add-vehicle.add-vehicle--layout {
    max-height: calc(100dvh - 140px);
    height: calc(100dvh - 140px);
  }
}
.add-vehicle.add-vehicle--layout .add-vehicle__toolbar,
.add-vehicle.add-vehicle--layout .add-vehicle__filters,
.add-vehicle.add-vehicle--layout .add-vehicle__list-heading {
  flex-shrink: 0;
}
.add-vehicle.add-vehicle--layout .add-vehicle__table-host {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.add-vehicle.add-vehicle--layout .add-vehicle__table-host .inventory-table-wrap {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.add-vehicle.add-vehicle--layout .add-vehicle__table-host .ReactTable.rt-table-in-viewport {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  height: 100% !important;
  max-height: 100% !important;
}
.add-vehicle.add-vehicle--layout .add-vehicle__table-host .ReactTable.rt-table-in-viewport .table-scroll-wrapper.rt-scroll-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}
.add-vehicle.add-vehicle--layout .add-vehicle__table-host .ReactTable.rt-table-in-viewport .pagination-top {
  flex-shrink: 0;
}
`;

const STICKY_STOCK_TABLE_CSS = `
.add-vehicle .sticky-stock-table .rt-table {
  width: 100%;
  min-width: 100%;
  border-collapse: collapse;
}
.add-vehicle .sticky-stock-table .rt-thead .rt-th:not(:last-child) {
  text-align: center !important;
}
.add-vehicle .sticky-stock-table .rt-thead .rt-th:last-child {
  text-align: right !important;
}
.add-vehicle .sticky-stock-table .rt-thead .rt-th:not(:last-child) .rt-resizable-header-content {
  text-align: center;
  width: 100%;
  box-sizing: border-box;
}
.add-vehicle .sticky-stock-table .rt-thead .rt-th:last-child .rt-resizable-header-content {
  text-align: right;
  width: 100%;
}
.add-vehicle .sticky-stock-table .rt-tbody .rt-td:not(:last-child) {
  text-align: center;
  vertical-align: middle;
}
.add-vehicle .sticky-stock-table .rt-tbody .rt-td:last-child {
  text-align: right;
  vertical-align: middle;
}
${getStickyStockTableStickyColumnCss(".add-vehicle")}
`;

const AddVehicle = ({
  activeColor = "blue",
  onClose,
  onVehicleAdded,
  showEmptyOption = false,
  isLightTheme = true,
  isTheme2 = false,
}) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const resolvedSelectStyles = useMemo(() => {
    if (isTheme2) {
      const theme2Styles = getTheme2SearchFilterSingleSelectStyles(activeColorStyle, true);
      return theme2Styles
        ? {
            ...theme2Styles,
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          }
        : getSelectStyles(activeColorStyle, isLightTheme);
    }
    return getSelectStyles(activeColorStyle, isLightTheme);
  }, [activeColorStyle, isTheme2, isLightTheme]);
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".add-vehicle.add-vehicle--theme2",
            fieldsSelector: ".add-vehicle-fields",
          })
        : "",
    [isTheme2]
  );
  const inputStyle = isTheme2 ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } : undefined;
  const [search, setSearch] = useState("");
  const [status] = useState("available");
  const [location, setLocation] = useState("");
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [altLocation] = useState("");
  const { data: inventories, isLoading } = useGetInventories(
    debouncedSearch,
    status,
    location,
    altLocation,
    page,
    pageSize
  );
  const { data: locationsData } = useGetChannels("sales_location_branch");

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handler);
  }, [search]);

  const columns = useMemo(
    () => buildInventorySearchColumns({ t, showNotification }),
    [t, showNotification]
  );

  const inventoryData = useMemo(
    () => mapInventorySearchResults(inventories?.results),
    [inventories?.results]
  );

  const handleClose = () => {
    onClose();
  };

  const showEmptyVehicleOption =
    showEmptyOption && !isLoading && inventoryData.length === 0;

  const layoutStyle = useMemo(
    () => ({
      "--rt-viewport-chrome": "300px",
    }),
    []
  );

  return (
    <div
      className={classNames("add-vehicle", "add-vehicle--layout", {
        "add-vehicle--theme2": isTheme2,
      })}
      style={layoutStyle}
    >
      <style>
        {getAddVehicleLayoutCss()}
        {STICKY_STOCK_TABLE_CSS}
        {theme2FieldsCss}
      </style>

      <div className="add-vehicle__toolbar d-flex justify-content-between align-items-center mb-2">
        <h4 className="mb-0">Add Vehicle from Inventory</h4>
        <Button color="secondary" onClick={handleClose}>
          Cancel
        </Button>
      </div>

      <Form className={classNames("add-vehicle__filters", { "add-vehicle-fields": isTheme2 })}>
        <Row>
          <Col md="4" className="mb-2">
            <FormGroup>
              <Label>Search:</Label>
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search make, model, VIN, stock..."
                className="form-control"
                style={inputStyle}
              />
            </FormGroup>
          </Col>
          <Col md="4" className="mb-2">
            <FormGroup>
              <Label>Location:</Label>
              <Select
                activeColor={activeColor}
                styles={resolvedSelectStyles}
                className="react-select"
                placeholder="Select location"
                name="location"
                isSearchable={false}
                closeMenuOnSelect={true}
                value={
                  location
                    ? {
                        value: location,
                        label:
                          locationsData?.results?.find(
                            (loc) => String(loc.id) === String(location)
                          )?.name || location,
                      }
                    : null
                }
                options={[
                  ...(locationsData?.results?.map((loc) => ({
                    value: String(loc.id),
                    label: loc.name,
                  })) || []),
                ]}
                onChange={(option) => setLocation(option?.value || "")}
                isClearable
              />
            </FormGroup>
          </Col>
        </Row>
      </Form>

      <h4 className="add-vehicle__list-heading mb-2">Inventory List</h4>

      {showEmptyVehicleOption ? (
        <div className="text-center py-2">
          <div
            className="p-4 d-flex align-items-center justify-content-between"
            style={{
              backgroundColor: isLightTheme ? "#fff" : "rgba(255, 255, 255, 0.05)",
              border: `1px solid ${isLightTheme ? "#e0e0e0" : "rgba(255, 255, 255, 0.1)"}`,
              cursor: "pointer",
            }}
            onClick={() => {
              if (onVehicleAdded) {
                onVehicleAdded([{ is_empty: true }]);
              }
              onClose();
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = activeColor;
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = isLightTheme ? "#e0e0e0" : "rgba(255, 255, 255, 0.1)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <div className="d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center mr-3"
                style={{
                  width: "40px",
                  height: "40px",
                  border: `1px solid ${isLightTheme ? "#333" : "#fff"}`,
                  opacity: 0.7,
                }}
              >
                <i className="tim-icons icon-delivery-fast" />
              </div>
              <div className="text-left">
                <div
                  className="font-weight-bold"
                  style={{ fontSize: "14px", color: isLightTheme ? "#333" : "#fff" }}
                >
                  Any Year Any Make Any Model
                </div>
                <div style={{ fontSize: "13px", color: "#888" }}>Any Color</div>
                <div className="mt-2 font-weight-bold" style={{ fontSize: "13px" }}>
                  You Can Select This if the Vehicle you are Looking for is Not in Stock
                </div>
              </div>
            </div>
            <div style={{ color: activeColor }}>
              <i className="tim-icons icon-simple-add" style={{ fontSize: "20px" }} />
            </div>
          </div>
        </div>
      ) : (
        <div className="add-vehicle__table-host">
          <div
            className="inventory-table-wrap"
            style={{ fontSize: "13px", minWidth: 0, maxWidth: "100%" }}
          >
            <ReactTable2
              activeColor={activeColor}
              data={inventoryData}
              columns={columns}
              disableViewportClamp={false}
              className={classNames(
                "-striped",
                "-highlight",
                "rt-table-in-viewport",
                { "sticky-stock-table": isTheme2 }
              )}
              manualPagination={true}
              pageCount={inventories?.pagination?.pageCount || 1}
              pageIndex={page - 1}
              totalItems={inventories?.pagination?.total}
              noDataText={t("searchInventory.noRecordsFound")}
              loading={isLoading}
              loadingText={t("searchInventory.loadingSearchInventory")}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              pageSize={pageSize}
              onPageChange={(pageIndex) => setPage(pageIndex + 1)}
              onRowClick={(row) => {
                if (row?.documentId || row?.id) {
                  if (onVehicleAdded) {
                    onVehicleAdded([toVehicleSelectionRow(row)]);
                  }
                  onClose();
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AddVehicle;
